import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { Calculator, Repeat2 } from 'lucide-react';
import BaseTicketScreen from './BaseTicketScreen';
import {
  FormRow, FormGroup, Label, Input, Select, FieldError,
  AutocompleteWrapper, AutocompleteDropdown, SuggestionItem, SuggestionCusip, SuggestionSecurity,
} from './shared/TicketStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate } from '../../utils/settlement';
import { parseAmountShorthand } from '../../utils/amountUtils';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  StructuredBand,
  StructuredHeroPanel,
  StructuredWorkspace,
  formatCurrencyValue,
} from './shared/StructuredCreditTicketLayout';

import { DateField } from '../shared/DateField';

/**
 * Repo trade entry — BaseTicketScreen render-prop ticket (dense 3-up grid like the
 * swap tickets) with the full lifecycle, comments, attachments and compliance
 * what-if. Booking is the two-step prepare flow: buildTransactionData runs the
 * cash legs + mints the repo contract + synthetic security via
 * /api/derivatives/repo/prepare, then returns a trade payload carrying
 * ticketType=REPO / eventType=ACQUIRE / sourceRef so the posting engine routes to
 * RepoPostingSpi (secured financing) at POST.
 *
 * Repo E2E remediation 2026-08-27:
 *  - F-REPO-016/017: collateral CUSIP is a security-master autocomplete
 *    (FixedIncomeTicketScreen pattern) that auto-fills description + dirty price.
 *  - F-REPO-003: amount/netAmount = the prepared contract's OPENING CASH — the
 *    real borrow/loan — not collateral face.
 *  - F-REPO-009: a draft re-save UPDATES the prepared contract in place
 *    (PUT /prepare/{id}) instead of minting a new contract + SMF per save.
 *  - F-REPO-018: the repo band is the single source of settlement truth — the
 *    Execution & Clearing fields auto-fill from it (counterparty → financing
 *    broker, composed settlement instruction, cleared ⇔ settlement type).
 *  - F-REPO-007: margin-call threshold captured (maintenance margin %).
 *  - F-REPO-014: counterparty offered from the counterparty dimension.
 *  - F-REPO-015: unsupported OPEN / TOM_NEXT term types no longer offered.
 */

/**
 * A repo trades and settles on its currency's bond calendar (SIFMA for USD, ADR 0009). The start
 * date is the trade date and the settle date the ticket sends; the hidden tradeDate / settleDate
 * (T+1) are only the fallback buildTransactionData keeps for a missing start date.
 */
const repoTradeDate = (currency?: string | null) => getTodayDate(calendarsFor('REPO', currency));
const repoFallbackSettleDate = (tradeDate: string, currency?: string | null) =>
  provisionalSettleDate({ tradeDate, ticketType: 'REPO', convention: 'T+1', currency: currency || 'USD' });

const initialFormData = {
  portfolio: '', assetManager: '', portfolioManager: '',
  repoType: 'REVERSE_REPO', termType: 'TERM',
  collateralCusip: '', collateralDescription: '', collateralFace: '', collateralDirtyPrice: '', haircutPct: '',
  marginCallThreshold: '',
  repoRate: '', termDays: '', startDate: repoTradeDate('USD'), endDate: '',
  counterpartyName: '', triPartyAgent: '', settlementType: 'BILATERAL',
  currency: 'USD', tradeDate: repoTradeDate('USD'), settleDate: repoFallbackSettleDate(repoTradeDate('USD'), 'USD'),
  submittedBy: getCurrentOperatorName(), executedBy: '', notes: '',
  _repoContractId: '',
};

const numOrNull = (v: any) => (v === '' || v == null ? null : Number(v));
const isProvider = (t: string) => t === 'REVERSE_REPO' || t === 'BUY_SELLBACK';

const REPO_TYPES = [
  ['REVERSE_REPO', 'Reverse Repo (lend cash)'], ['REPO', 'Repo (borrow cash)'],
  ['BUY_SELLBACK', 'Buy / Sell-back'], ['SELL_BUYBACK', 'Sell / Buy-back'],
];

// Tri-party agents are custodial platforms, not trading counterparties — the
// counterparty dimension has no custodian type, so the real tri-party universe
// is curated here (operator finding 2026-08-27).
const TRI_PARTY_AGENTS = ['BNY Mellon', 'J.P. Morgan', 'Euroclear Bank', 'Clearstream Banking'];

/** Collateral face accepts blotter shorthand — platform convention (amountUtils): M=thousand, MM=million, MMM=billion. */
const parseFace = (v: any): number => parseAmountShorthand(v);

const RepoActionBar = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 14px;
`;

const RepoPreviewButton = styled.button`
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  border-radius: 10px;
  border: 1px solid rgba(6, 182, 212, 0.34);
  background: rgba(6, 182, 212, 0.12);
  color: #67e8f9;
  cursor: pointer;
  font-weight: 850;
  font-size: 12px;

  &:disabled {
    opacity: 0.58;
    cursor: not-allowed;
  }
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 16px;
`;

const PreviewCell = styled.div`
  background: rgba(2, 6, 23, 0.35);
  border: 1px solid rgba(71, 85, 105, 0.38);
  border-radius: 11px;
  padding: 12px 13px;
`;

const PreviewLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const PreviewValue = styled.div<{ $highlight?: string }>`
  color: ${p => p.$highlight || '#f8fafc'};
  font-size: 16px;
  font-weight: 900;
  line-height: 1.24;
  overflow-wrap: anywhere;
`;

/** Auto-composed settlement instruction (F-REPO-018) — the repo band is the source. */
const composeSettlementInstruction = (fd: any): string => {
  const cp = fd.counterpartyName?.trim();
  if (fd.settlementType === 'TRI_PARTY') {
    const agent = fd.triPartyAgent?.trim();
    return `Tri-party${agent ? ` via ${agent}` : ''}${cp ? ` vs ${cp}` : ''}`;
  }
  if (fd.settlementType === 'CLEARED') {
    return `Cleared repo${cp ? ` vs ${cp}` : ''}`;
  }
  return `Bilateral DVP${cp ? ` vs ${cp}` : ''}`;
};

const RepoTicketScreen: React.FC = () => {
  const validateForm = (formData: any, action: string) => {
    const e: { [k: string]: string } = {};
    if (!formData.portfolio?.trim()) e.portfolio = 'Portfolio is required';
    if (!formData.collateralFace || parseFace(formData.collateralFace) <= 0) e.collateralFace = 'Collateral face > 0 required (shorthand ok: 5MM)';
    if (!formData.collateralDirtyPrice || Number(formData.collateralDirtyPrice) <= 0) e.collateralDirtyPrice = 'Dirty price > 0 required';
    if (formData.repoRate === '' || formData.repoRate == null) e.repoRate = 'Repo rate is required';
    if (!formData.termDays && !(formData.startDate && formData.endDate)) e.termDays = 'Term days or start+end required';
    // The start date is the trade and settle date; a date that is not a business day on the repo's
    // calendar leaves the field empty, and must not fall back to the default date.
    if (!formData.startDate) e.startDate = 'Start date is required (it is the trade and settle date)';
    if (!isProvider(formData.repoType) && !formData.collateralCusip?.trim()) {
      e.collateralCusip = 'A repo pledges our collateral — pick the held security';
    }
    if (formData.settlementType === 'TRI_PARTY' && !formData.triPartyAgent?.trim()) {
      e.triPartyAgent = 'Tri-party settlement requires the agent';
    }
    if (action === 'execute' && !formData.executedBy?.trim()) e.executedBy = 'Executed By is required for execution';
    return e;
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);
    const provider = isProvider(formData.repoType);

    const prepareBody = {
      portfolioId: portfolio.id,
      repoType: formData.repoType, termType: formData.termType,
      collateralCusip: formData.collateralCusip?.trim() || null, collateralDescription: formData.collateralDescription?.trim() || null,
      collateralFace: parseFace(formData.collateralFace) || null, collateralDirtyPrice: numOrNull(formData.collateralDirtyPrice),
      haircutPct: numOrNull(formData.haircutPct), marginCallThreshold: numOrNull(formData.marginCallThreshold),
      repoRate: numOrNull(formData.repoRate),
      termDays: numOrNull(formData.termDays), startDate: formData.startDate || null, endDate: formData.endDate || null,
      counterpartyName: formData.counterpartyName?.trim() || null, triPartyAgent: formData.triPartyAgent?.trim() || null,
      settlementType: formData.settlementType, currency: formData.currency || 'USD', tradeDate: formData.startDate,
    };
    // F-REPO-009: a re-saved draft updates its existing contract in place.
    const prepareRes = formData._repoContractId
      ? await axios.put(`/api/derivatives/repo/prepare/${formData._repoContractId}`, prepareBody)
      : await axios.post('/api/derivatives/repo/prepare', prepareBody);
    const { securityId, sourceRef, openingCash } = prepareRes.data;

    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager?.trim()) {
      const pm = dimensionData.portfolioManagers.find((x: any) => x.portfolioManagerName === formData.portfolioManager.trim());
      if (pm?.id) portfolioManagerId = pm.id;
    }
    let assetManagerId: number | null = null;
    if (formData.assetManager?.trim()) {
      const am = dimensionData.assetManagers.find((x: any) => x.assetManagerName === formData.assetManager.trim());
      if (am?.id) assetManagerId = am.id;
    }

    // F-REPO-003: the trade's economics are the OPENING CASH (the actual
    // borrow/loan), not the collateral face. Server-side the REPO ticket type
    // is exempt from the bond principal recompute (F-FUT-003 guard).
    const cash = openingCash != null ? Number(openingCash) : parseFace(formData.collateralFace);
    return {
      transactionType: provider ? 'BUY' : 'SELL',
      securityId, portfolioId: portfolio.id, assetManagerId, portfolioManagerId,
      quantity: 1, price: Number(formData.collateralDirtyPrice), amount: cash,
      netAmount: cash, commission: 0,
      tradeDate: formData.startDate || formData.tradeDate, settleDate: formData.startDate || formData.settleDate,
      currency: formData.currency || 'USD', status: 'DRAFT', ticketType: 'REPO', eventType: 'ACQUIRE', sourceRef,
      createdBy: formData.submittedBy, executedBy: formData.executedBy || null,
      notes: formData.notes || `${formData.repoType} collateral ${formData.collateralCusip || ''} ${formData.collateralFace} @ ${formData.repoRate}%`,
    };
  };

  const loadTransactionData = async (t: any) => {
    const base = {
      ...initialFormData,
      portfolio: t.portfolioName || '', assetManager: t.assetManagerName || '', portfolioManager: t.portfolioManagerName || '',
      submittedBy: t.createdBy || getCurrentOperatorName(),
      executedBy: t.executedBy || '', notes: t.notes || '',
      tradeDate: t.tradeDate || repoTradeDate(t.currency),
      settleDate: t.settleDate || repoFallbackSettleDate(t.tradeDate || repoTradeDate(t.currency), t.currency),
      currency: t.currency || 'USD',
    };
    const m = (t.sourceRef || '').match(/repo-contract:(\d+)/);
    if (!m) return base;
    try {
      const c = (await axios.get(`/api/derivatives/repo/contract/${m[1]}`)).data;
      return {
        ...base,
        _repoContractId: m[1],
        repoType: c.repoType || 'REVERSE_REPO', termType: c.termType || 'TERM',
        collateralCusip: c.collateralCusip || '', collateralDescription: c.collateralDescription || '',
        collateralFace: c.collateralFace != null ? String(c.collateralFace) : '',
        collateralDirtyPrice: c.collateralDirtyPrice != null ? String(c.collateralDirtyPrice) : '',
        haircutPct: c.haircutPct != null ? String(c.haircutPct) : '',
        marginCallThreshold: c.marginCallThreshold != null ? String(c.marginCallThreshold) : '',
        repoRate: c.repoRate != null ? String(Number(c.repoRate) * 100) : '',
        termDays: c.termDays != null ? String(c.termDays) : '',
        startDate: c.startDate || base.startDate, endDate: c.endDate || '',
        counterpartyName: c.counterpartyName || '', triPartyAgent: c.triPartyAgent || '',
        settlementType: c.settlementType || 'BILATERAL',
      };
    } catch { return base; }
  };

  return (
    <BaseTicketScreen
      title="Repo Trade Entry" assetClass="Financing" assetSubClass="Repo"
      initialFormData={initialFormData} validateForm={validateForm} buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, errors, handleInputChange, isFieldDisabled, dimensionData, transactionStatus }) => (
        <RepoFields formData={formData} errors={errors} handleInputChange={handleInputChange}
          isFieldDisabled={isFieldDisabled} dimensionData={dimensionData} transactionStatus={transactionStatus} />
      )}
    </BaseTicketScreen>
  );
};

const RepoFields: React.FC<{ formData: any; errors: any; handleInputChange: (f: string, v: string) => void; isFieldDisabled: (f: string) => boolean; dimensionData: any; transactionStatus: any; }>
  = ({ formData, errors, handleInputChange, isFieldDisabled, dimensionData, transactionStatus }) => {
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [cusipSuggestions, setCusipSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [counterparties, setCounterparties] = useState<any[]>([]);
  const dis = (f: string) => isFieldDisabled(f);

  // F-REPO-014: counterparty dimension feed (free text still allowed).
  useEffect(() => {
    let alive = true;
    axios.get('/api/credit/counterparties')
      .then(({ data }) => {
        if (alive) setCounterparties((Array.isArray(data) ? data : [])
          .filter((cp: any) => ['DEALER', 'BANK'].includes(cp.counterpartyType || '')));
      })
      .catch(() => { /* dimension optional — field stays free-form */ });
    return () => { alive = false; };
  }, []);

  // F-REPO-016: collateral CUSIP autocomplete (FixedIncomeTicketScreen pattern).
  const fetchCusipSuggestions = async (prefix: string) => {
    if (!prefix || prefix.length < 1) { setCusipSuggestions([]); setShowSuggestions(false); return; }
    try {
      const res = await axios.get(`/api/dimensions/securities/cusip/search?prefix=${encodeURIComponent(prefix)}`);
      const list = (res.data || []).filter((s: any) => s.securityType !== 'REPO');
      setCusipSuggestions(list);
      setShowSuggestions(list.length > 0);
    } catch { setCusipSuggestions([]); setShowSuggestions(false); }
  };

  // F-REPO-017: selection auto-fills description + dirty price from the SMF.
  const handleCollateralSelect = async (security: any) => {
    setShowSuggestions(false);
    handleInputChange('collateralCusip', security.cusip || '');
    if (security.securityName) handleInputChange('collateralDescription', security.securityName);
    try {
      const full = (await axios.get(`/api/dimensions/securities/cusip/${security.cusip}`)).data;
      if (full?.securityName) handleInputChange('collateralDescription', full.securityName);
      if (full?.currentPrice != null) handleInputChange('collateralDirtyPrice', String(full.currentPrice));
    } catch { /* suggestion data already applied */ }
  };

  // F-REPO-018: the repo band drives the Execution & Clearing fields. Only
  // fills a field that is empty or still holds our last auto value — an
  // operator override is never clobbered.
  const autoRef = useRef<{ broker: string; instruction: string; cleared: string }>({ broker: '', instruction: '', cleared: '' });
  useEffect(() => {
    if (transactionStatus && transactionStatus !== 'DRAFT') return;
    const auto = autoRef.current;
    const cp = formData.counterpartyName?.trim() || '';
    if (cp && (!formData.executionBroker || formData.executionBroker === auto.broker)) {
      handleInputChange('executionBroker', cp);
      auto.broker = cp;
    }
    if (!formData.executionMethod) {
      handleInputChange('executionMethod', 'OTC_BILATERAL');
    }
    const instruction = composeSettlementInstruction(formData);
    if (!formData.settlementInstruction || formData.settlementInstruction === auto.instruction) {
      handleInputChange('settlementInstruction', instruction);
      auto.instruction = instruction;
    }
    const cleared = formData.settlementType === 'CLEARED' ? 'true' : 'false';
    if (formData.isCleared == null || formData.isCleared === '' || String(formData.isCleared) === auto.cleared) {
      handleInputChange('isCleared', cleared);
      auto.cleared = cleared;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.counterpartyName, formData.settlementType, formData.triPartyAgent, transactionStatus]);

  const handleCalculate = async () => {
    try {
      setBusy(true);
      const res = await axios.post('/api/derivatives/repo/calculate', {
        repoType: formData.repoType, collateralFace: parseFace(formData.collateralFace) || null,
        collateralDirtyPrice: numOrNull(formData.collateralDirtyPrice), haircutPct: numOrNull(formData.haircutPct),
        repoRate: numOrNull(formData.repoRate), termDays: numOrNull(formData.termDays),
        startDate: formData.startDate || null, endDate: formData.endDate || null,
      });
      setPreview(res.data);
    } catch { setPreview(null); } finally { setBusy(false); }
  };
  const fmt = (v: any) => (v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));

  return (
    <StructuredWorkspace>
      <StructuredHeroPanel
        toneName="repo"
        icon={Repeat2}
        eyebrow="Repo Financing"
        title="Repo Ticket"
        description="Book secured financing with explicit collateral, haircut, repo rate, tenor, and settlement structure before the posting engine handles the cash and collateral legs."
        badges={['Secured financing', 'Collateralized cash legs', 'Prepare then trade']}
        signals={[
          { label: 'Repo Type', value: formData.repoType || '-', accent: true },
          { label: 'Collateral Face', value: formatCurrencyValue(parseFace(formData.collateralFace), formData.currency || 'USD'), accent: true },
          { label: 'Repo Rate', value: formData.repoRate ? `${formData.repoRate}%` : '-' },
          { label: 'Term', value: formData.termDays ? `${formData.termDays}d` : formData.termType || '-' },
        ]}
      />

      <StructuredBand>
        <BandHeader>
          <BandTitleGroup>
            <BandEyebrow $tone="repo">Trade Setup</BandEyebrow>
            <BandTitle>Managers, Execution, and Repo Terms</BandTitle>
            <BandSubline>Set ownership, repo direction, rate, and tenor before defining collateral.</BandSubline>
          </BandTitleGroup>
        </BandHeader>
      {/* Row 1 — managers + execution */}
      <FormRow>
        <FormGroup>
          <Label>Asset Manager</Label>
          <Select value={formData.assetManager || ''} disabled={dis('assetManager')} onChange={e => handleInputChange('assetManager', e.target.value)}>
            <option value="">Select Asset Manager</option>
            {(dimensionData?.assetManagers || []).map((am: any) => <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>)}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Portfolio Manager</Label>
          <Select value={formData.portfolioManager || ''} disabled={dis('portfolioManager')} onChange={e => handleInputChange('portfolioManager', e.target.value)}>
            <option value="">Select Portfolio Manager</option>
            {(dimensionData?.portfolioManagers || []).map((pm: any) => <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>)}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
          <Select value={formData.executedBy || ''} disabled={dis('executedBy')} onChange={e => handleInputChange('executedBy', e.target.value)} className={errors.executedBy ? 'error' : ''}>
            <option value="">Select</option>
            {(dimensionData?.users || []).map((u: any) => <option key={u.id} value={u.fullName}>{u.fullName}{u.role ? ` (${u.role})` : ''}</option>)}
          </Select>
          {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* Row 2 — repo terms */}
      <FormRow>
        <FormGroup>
          <Label>Repo Type*</Label>
          <Select value={formData.repoType} disabled={dis('repoType')} onChange={e => handleInputChange('repoType', e.target.value)}>
            {REPO_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Term Type</Label>
          <Select value={formData.termType} disabled={dis('termType')} onChange={e => handleInputChange('termType', e.target.value)}>
            <option value="TERM">Term</option><option value="OVERNIGHT">Overnight</option>
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Repo Rate* (%)</Label>
          <Input type="number" placeholder="5.30" value={formData.repoRate || ''} disabled={dis('repoRate')}
            onChange={e => handleInputChange('repoRate', e.target.value)} className={errors.repoRate ? 'error' : ''} />
          {errors.repoRate && <FieldError>{errors.repoRate}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* Row 3 — tenor */}
      <FormRow>
        <FormGroup>
          <Label>Term (days)</Label>
          <Input type="number" placeholder="30" value={formData.termDays || ''} disabled={dis('termDays')}
            onChange={e => handleInputChange('termDays', e.target.value)} className={errors.termDays ? 'error' : ''} />
          {errors.termDays && <FieldError>{errors.termDays}</FieldError>}
        </FormGroup>
        <FormGroup>
          <Label>Start Date*</Label>
          <DateField
            value={formData.startDate || ''}
            disabled={dis('startDate')}
            className={errors.startDate ? 'error' : ''}
            calendars={calendarsFor('REPO', formData.currency)}
            onChange={iso => handleInputChange('startDate', iso)}
          />
          {errors.startDate && <FieldError>{errors.startDate}</FieldError>}
        </FormGroup>
        <FormGroup>
          <Label>End Date (optional)</Label>
          <DateField
            value={formData.endDate || ''}
            disabled={dis('endDate')}
            onChange={iso => handleInputChange('endDate', iso)}
          />
        </FormGroup>
      </FormRow>
      </StructuredBand>

      <StructuredBand>
        <BandHeader>
          <BandTitleGroup>
            <BandEyebrow $tone="repo">Collateral Package</BandEyebrow>
            <BandTitle>Collateral Identity, Face, Dirty Price, Haircut, and Settlement</BandTitle>
            <BandSubline>Pick the collateral from the security master — a repo (cash taker) must pledge a security this portfolio holds.</BandSubline>
          </BandTitleGroup>
        </BandHeader>
      {/* Row 4 — collateral identity + face */}
      <FormRow>
        <FormGroup>
          <Label>Collateral CUSIP{!isProvider(formData.repoType) ? '*' : ''}</Label>
          <AutocompleteWrapper>
            <Input placeholder="Search CUSIP / name" value={formData.collateralCusip || ''} disabled={dis('collateralCusip')}
              autoComplete="off"
              onChange={e => {
                const v = e.target.value.toUpperCase();
                handleInputChange('collateralCusip', v);
                fetchCusipSuggestions(v);
              }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
              className={errors.collateralCusip ? 'error' : ''} />
            {showSuggestions && cusipSuggestions.length > 0 && (
              <AutocompleteDropdown>
                {cusipSuggestions.slice(0, 8).map((s: any) => (
                  <SuggestionItem key={s.cusip} onMouseDown={() => handleCollateralSelect(s)}>
                    <SuggestionCusip>{s.cusip}</SuggestionCusip>
                    <SuggestionSecurity>{s.securityName || s.securityType || ''}</SuggestionSecurity>
                  </SuggestionItem>
                ))}
              </AutocompleteDropdown>
            )}
          </AutocompleteWrapper>
          {errors.collateralCusip && <FieldError>{errors.collateralCusip}</FieldError>}
        </FormGroup>
        <FormGroup>
          <Label>Collateral Description</Label>
          <Input placeholder="auto-fills from the selected security" value={formData.collateralDescription || ''} disabled={dis('collateralDescription')}
            onChange={e => handleInputChange('collateralDescription', e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>Collateral Face*</Label>
          <Input type="text" placeholder="10MM" value={formData.collateralFace || ''} disabled={dis('collateralFace')}
            onChange={e => handleInputChange('collateralFace', e.target.value)}
            onBlur={() => {
              const parsed = parseFace(formData.collateralFace);
              if (parsed > 0) handleInputChange('collateralFace', String(parsed));
            }}
            className={errors.collateralFace ? 'error' : ''} />
          {errors.collateralFace && <FieldError>{errors.collateralFace}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* Row 5 — price / haircut / margin threshold */}
      <FormRow>
        <FormGroup>
          <Label>Dirty Price* (% of par)</Label>
          <Input type="number" placeholder="101.25" value={formData.collateralDirtyPrice || ''} disabled={dis('collateralDirtyPrice')}
            onChange={e => handleInputChange('collateralDirtyPrice', e.target.value)} className={errors.collateralDirtyPrice ? 'error' : ''} />
          {errors.collateralDirtyPrice && <FieldError>{errors.collateralDirtyPrice}</FieldError>}
        </FormGroup>
        <FormGroup>
          <Label>Haircut (%)</Label>
          <Input type="number" placeholder="2.0" value={formData.haircutPct || ''} disabled={dis('haircutPct')}
            onChange={e => handleInputChange('haircutPct', e.target.value)} />
        </FormGroup>
        <FormGroup>
          <Label>Margin Call Threshold (%)</Label>
          <Input type="number" placeholder="defaults to haircut" value={formData.marginCallThreshold || ''} disabled={dis('marginCallThreshold')}
            onChange={e => handleInputChange('marginCallThreshold', e.target.value)} />
        </FormGroup>
      </FormRow>

      {/* Row 6 — counterparty / settlement / agent */}
      <FormRow>
        <FormGroup>
          <Label>Counterparty</Label>
          <Select value={formData.counterpartyName || ''} disabled={dis('counterpartyName')}
            onChange={e => handleInputChange('counterpartyName', e.target.value)}>
            <option value="">Select counterparty</option>
            {formData.counterpartyName
              && !counterparties.some((cp: any) => cp.counterpartyName === formData.counterpartyName) && (
              <option value={formData.counterpartyName}>{formData.counterpartyName}</option>
            )}
            {counterparties.map((cp: any) => (
              <option key={cp.id} value={cp.counterpartyName}>{cp.counterpartyName}{cp.counterpartyType ? ` (${cp.counterpartyType})` : ''}</option>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Settlement Type</Label>
          <Select value={formData.settlementType} disabled={dis('settlementType')} onChange={e => handleInputChange('settlementType', e.target.value)}>
            <option value="BILATERAL">Bilateral (DVP)</option><option value="TRI_PARTY">Tri-party</option>
            <option value="CLEARED">Cleared (FICC / LCH)</option>
          </Select>
        </FormGroup>
        <FormGroup>
          <Label>Tri-party Agent{formData.settlementType === 'TRI_PARTY' ? '*' : ''}</Label>
          <Select value={formData.triPartyAgent || ''} disabled={dis('triPartyAgent') || formData.settlementType !== 'TRI_PARTY'}
            onChange={e => handleInputChange('triPartyAgent', e.target.value)} className={errors.triPartyAgent ? 'error' : ''}>
            <option value="">Select agent</option>
            {formData.triPartyAgent && !TRI_PARTY_AGENTS.includes(formData.triPartyAgent) && (
              <option value={formData.triPartyAgent}>{formData.triPartyAgent}</option>
            )}
            {TRI_PARTY_AGENTS.map(a => <option key={a} value={a}>{a}</option>)}
          </Select>
          {errors.triPartyAgent && <FieldError>{errors.triPartyAgent}</FieldError>}
        </FormGroup>
      </FormRow>

      {/* Row 7 — currency */}
      <FormRow>
        <FormGroup>
          <Label>Currency</Label>
          <Input value={formData.currency || ''} disabled={dis('currency')} onChange={e => handleInputChange('currency', e.target.value.toUpperCase())} />
        </FormGroup>
      </FormRow>
      </StructuredBand>

      <StructuredBand>
        <BandHeader>
          <BandTitleGroup>
            <BandEyebrow $tone="repo">Cash Leg Preview</BandEyebrow>
            <BandTitle>Financing Cash and Collateral Preview</BandTitle>
            <BandSubline>Preview calculated cash legs from the repo calculation endpoint before saving the ticket.</BandSubline>
          </BandTitleGroup>
        </BandHeader>
      <RepoActionBar>
        <RepoPreviewButton type="button" onClick={handleCalculate} disabled={busy}>
          <Calculator size={16} />
          {busy ? 'Calculating...' : 'Preview Cash Legs'}
        </RepoPreviewButton>
      </RepoActionBar>
      {preview && (
        <PreviewGrid>
          <Cell label={`${preview.direction}`} value={`${preview.termDays}d`} />
          <Cell label="Opening Cash" value={`$${fmt(preview.openingCash)}`} />
          <Cell label="Repo Interest" value={`$${fmt(preview.repoInterest)}`} />
          <Cell label="Closing Cash" value={`$${fmt(preview.closingCash)}`} />
          <Cell label="Collateral MV" value={`$${fmt(preview.collateralMarketValue)}`} />
          <Cell label="Position MV" value={`$${fmt(preview.positionMarketValue)}`} highlight={Number(preview.positionMarketValue) >= 0 ? '#10b981' : '#f87171'} />
          <Cell label="Cash @ Start" value={`$${fmt(preview.cashImpactAtStart)}`} />
        </PreviewGrid>
      )}
      </StructuredBand>
    </StructuredWorkspace>
  );
};

const Cell: React.FC<{ label: string; value: string; highlight?: string }> = ({ label, value, highlight }) => (
  <PreviewCell>
    <PreviewLabel>{label}</PreviewLabel>
    <PreviewValue $highlight={highlight}>{value}</PreviewValue>
  </PreviewCell>
);

export default RepoTicketScreen;
